import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronRight, Link2, Trash2 } from 'lucide-react';

import {
  useCreateAccountWithReferral,
  useCreateReferralCode,
  useDeleteReferralCode,
  usePatientSearch,
  useReferralCodes,
  useReferralSummary,
  type ReferralSummary,
} from '@/lib/api/referralHooks';
import { Td, Th, referralLink, usd } from './referralShared';

const PAGE_SIZE = 25;

export function AdminReferralsScreen() {
  const navigate = useNavigate();
  const summary = useReferralSummary();
  const create = useCreateReferralCode();
  const del = useDeleteReferralCode();

  const [email, setEmail] = useState('');
  const [lastAction, setLastAction] = useState<string | null>(null);

  // Table controls — the codes list is server-paginated (1.5k+ codes;
  // fetching them all is what broke the original page).
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [page, setPage] = useState(0);
  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedQ(q);
      setPage(0);
    }, 300);
    return () => window.clearTimeout(t);
  }, [q]);

  const { data, isLoading, isError, isFetching } = useReferralCodes({
    q: debouncedQ,
    activeOnly,
    page,
    pageSize: PAGE_SIZE,
  });
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  async function generate(forEmail: string) {
    const trimmed = forEmail.trim();
    if (!trimmed) return;
    setLastAction(null);
    try {
      const result = await create.mutateAsync(trimmed);
      if (result.created && result.code) {
        setLastAction(`Code for ${trimmed}: ${result.code}`);
        setEmail('');
      } else if (result.reason === 'patient_not_found') {
        setLastAction(
          `No patient with email ${trimmed} — they need to sign up first.`,
        );
      } else {
        setLastAction(`Failed: ${result.reason || 'unknown'}`);
      }
    } catch (err) {
      setLastAction(`Failed: ${String(err)}`);
    }
  }

  return (
    <div className="flex flex-col gap-24">
      <div className="flex flex-col gap-8">
        <h1 className="type-heading-xl">Referrals</h1>
        <p className="type-body-md text-ink-muted max-w-content-sm">
          Each customer gets one referral code — their friends get{' '}
          <strong>$20 off a first order</strong> when they use it (this works
          at checkout today). As those friends' orders ship, the referrer
          earns <strong>$20 of store credit for every treatment that
          ships</strong>, across everyone they referred, with no cap. Referrers
          spend that credit from the Referral Program page in their portal; it
          becomes account credit and comes off their next order or renewal.
        </p>
      </div>

      {/* ---- Program KPIs ---- */}
      <SummaryCards summary={summary.data} isLoading={summary.isLoading} />

      {/* ---- New account + referral code (influencer / partner self-serve) ---- */}
      <CreateAccountCard />

      {/* ---- Coupon creation ---- */}
      <div className="p-16 border border-line rounded-lg bg-raised">
        <h2 className="type-heading-md mb-4">Generate a referral code</h2>
        <p className="type-label-sm text-ink-muted mb-12">
          Search a customer by name or email, or paste a full email. They must
          already have a patient row (signed up at least once). Idempotent —
          re-running returns their existing code.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            generate(email);
          }}
          className="flex flex-wrap items-center gap-8"
        >
          <CustomerCombobox
            value={email}
            onChange={setEmail}
            onPick={(picked) => {
              setEmail(picked);
              generate(picked);
            }}
          />
          <button
            type="submit"
            disabled={create.isPending || !email.trim()}
            className="px-14 py-6 type-label-sm bg-ink text-ink-onAccent rounded hover:opacity-90 disabled:opacity-50"
          >
            {create.isPending ? 'Generating…' : 'Generate code'}
          </button>
        </form>
        {lastAction && (
          <p className="type-label-sm text-ink-muted mt-8">{lastAction}</p>
        )}
      </div>

      {/* ---- Conversion tracking (server-paginated) ---- */}
      <div className="border border-line rounded-lg bg-raised overflow-hidden">
        <div className="p-16 border-b border-line flex flex-wrap items-center gap-12">
          <h2 className="type-heading-md">Conversion tracking</h2>
          <input
            type="search"
            placeholder="Search code, name or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="bg-sunken border border-line rounded px-10 py-6 type-label-sm outline-none min-w-[220px]"
          />
          <label className="flex items-center gap-6 type-label-sm text-ink-muted cursor-pointer select-none">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => {
                setActiveOnly(e.target.checked);
                setPage(0);
              }}
            />
            Only codes with referrals
          </label>
          <span className="ml-auto type-label-sm text-ink-muted">
            {isFetching ? 'Loading…' : `${total} code${total === 1 ? '' : 's'}`}
          </span>
        </div>

        {isLoading && (
          <div className="p-16 text-center text-ink-muted type-label-md">
            Loading…
          </div>
        )}
        {isError && (
          <div className="p-16 text-center text-ink-muted">Failed to load.</div>
        )}

        {data && (
          <div className="overflow-x-auto">
            <table className="w-full type-label-sm">
              <thead className="bg-sunken border-b border-line text-left text-ink-muted">
                <tr>
                  <Th>Code</Th>
                  <Th>Customer</Th>
                  <Th align="right">Referrals</Th>
                  <Th align="right">Orders placed</Th>
                  <Th align="right">Orders shipped</Th>
                  <Th align="right">Credit earned</Th>
                  <Th />
                  <Th />
                </tr>
              </thead>
              <tbody>
                {data.codes.map((c) => (
                  <tr
                    key={c.code}
                    onClick={() =>
                      navigate(`/admin/referrals/${encodeURIComponent(c.code)}`)
                    }
                    className="border-t border-line-subtle cursor-pointer hover:bg-sunken"
                  >
                    <Td>
                      <div className="flex items-center gap-8">
                        <span className="font-mono font-semibold">
                          {c.code}
                        </span>
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            const link = referralLink(c.code);
                            try {
                              await navigator.clipboard.writeText(link);
                              setLastAction(`Link copied — ${link}`);
                            } catch {
                              setLastAction(`Share link: ${link}`);
                            }
                          }}
                          className="inline-flex items-center gap-4 px-6 py-3 rounded border border-line text-ink-muted hover:bg-sunken hover:text-ink"
                          title="Copy share link"
                        >
                          <Link2 size={12} strokeWidth={1.5} />
                          Link
                        </button>
                      </div>
                    </Td>
                    <Td>
                      <div>{c.customer_name || '—'}</div>
                      <div className="text-ink-muted text-11">
                        {c.customer_email || '—'}
                      </div>
                    </Td>
                    <Td align="right">{c.referrals_sent}</Td>
                    <Td align="right">{c.orders_placed}</Td>
                    <Td align="right">{c.orders_shipped}</Td>
                    <Td align="right">
                      <span className="font-semibold">
                        {usd(c.credit_earned_cents)}
                      </span>
                    </Td>
                    <Td align="right">
                      <button
                        type="button"
                        disabled={del.isPending}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (
                            !confirm(
                              `Delete referral code ${c.code}? This deactivates the $20 discount.`,
                            )
                          )
                            return;
                          setLastAction(null);
                          try {
                            const r = await del.mutateAsync(c.code);
                            if (r.deleted) {
                              setLastAction(`Deleted code ${c.code}.`);
                            } else if (r.reason === 'has_redemptions') {
                              setLastAction(
                                `Can't delete ${c.code} — it has ${r.redemptions} redemption(s). Codes with usage history are kept.`,
                              );
                            } else {
                              setLastAction(
                                `Delete failed: ${r.reason || 'unknown'}`,
                              );
                            }
                          } catch (err) {
                            setLastAction(`Delete failed: ${String(err)}`);
                          }
                        }}
                        className="inline-flex items-center gap-4 px-8 py-4 rounded border border-line hover:bg-sunken disabled:opacity-40"
                        title="Delete code"
                      >
                        <Trash2 size={12} strokeWidth={1.5} />
                      </button>
                    </Td>
                    <Td align="right">
                      <ChevronRight size={14} strokeWidth={1.5} />
                    </Td>
                  </tr>
                ))}
                {data.codes.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="p-16 text-center text-ink-muted"
                    >
                      {activeOnly
                        ? 'No codes with referral activity match. Untick "Only codes with referrals" to browse every issued code.'
                        : 'No referral codes match.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {data && pageCount > 1 && (
          <div className="p-12 border-t border-line flex items-center justify-between type-label-sm">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="px-10 py-5 rounded border border-line hover:bg-sunken disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-ink-muted">
              Page {page + 1} of {pageCount}
            </span>
            <button
              type="button"
              disabled={page + 1 >= pageCount}
              onClick={() => setPage((p) => p + 1)}
              className="px-10 py-5 rounded border border-line hover:bg-sunken disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** High-level program KPIs. One aggregated backend call — stays fast no
 *  matter how many codes exist. */
function SummaryCards({
  summary,
  isLoading,
}: {
  summary: ReferralSummary | undefined;
  isLoading: boolean;
}) {
  const pct =
    summary && summary.referred_customers > 0
      ? Math.round(
          (summary.referred_with_order / summary.referred_customers) * 100,
        )
      : 0;
  const cards = [
    {
      label: 'Referrals sent',
      value: summary?.referrals_sent,
      sub: summary ? `+${summary.referrals_30d} in the last 30 days` : null,
    },
    {
      label: 'Referred → ordered',
      value: summary ? `${summary.referred_with_order}/${summary.referred_customers}` : undefined,
      sub: summary ? `${pct}% of referred customers ordered` : null,
    },
    {
      label: 'Orders from referrals',
      value: summary?.orders_placed,
      sub: summary ? `${summary.orders_shipped} shipped` : null,
    },
    {
      label: 'Credit earned',
      value: summary ? usd(summary.credit_earned_cents) : undefined,
      sub: summary ? `across ${summary.codes_active} active codes` : null,
    },
    {
      label: 'Codes issued',
      value: summary?.codes_total,
      sub: summary ? `${summary.codes_active} with ≥1 referral` : null,
    },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-12">
      {cards.map((c) => (
        <div
          key={c.label}
          className="p-14 border border-line rounded-lg bg-raised flex flex-col gap-4"
        >
          <span className="text-11 uppercase tracking-wide text-ink-muted">
            {c.label}
          </span>
          <span className="type-heading-md">
            {isLoading || c.value === undefined ? '—' : c.value}
          </span>
          {c.sub && (
            <span className="type-label-sm text-ink-muted">{c.sub}</span>
          )}
        </div>
      ))}
    </div>
  );
}

/** Customer search-as-you-type for the code-generation form. Reuses the
 *  clinical-admin patient search endpoint. */
function CustomerCombobox({
  value,
  onChange,
  onPick,
}: {
  value: string;
  onChange: (v: string) => void;
  onPick: (email: string) => void;
}) {
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), 300);
    return () => window.clearTimeout(t);
  }, [value]);

  const { data, isFetching } = usePatientSearch(debounced);
  const results = data?.results ?? [];

  return (
    <div className="relative flex-1 min-w-[260px]">
      <input
        type="text"
        required
        placeholder="Search name or email…"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Delay so a click on a suggestion registers first.
          blurTimer.current = window.setTimeout(() => setOpen(false), 150);
        }}
        className="w-full bg-sunken border border-line rounded px-10 py-6 type-label-sm outline-none focus:outline-none"
      />
      {open && debounced.trim().length >= 2 && (
        <div className="absolute z-20 mt-2 w-full bg-raised border border-line rounded-lg shadow-lg overflow-hidden">
          {isFetching && results.length === 0 && (
            <div className="px-10 py-8 type-label-sm text-ink-muted">
              Searching…
            </div>
          )}
          {!isFetching && results.length === 0 && (
            <div className="px-10 py-8 type-label-sm text-ink-muted">
              No customers match.
            </div>
          )}
          {results.map((r) => (
            <button
              key={r.id}
              type="button"
              onMouseDown={(e) => {
                // mousedown beats the input's blur — pick before it closes.
                e.preventDefault();
                if (blurTimer.current) window.clearTimeout(blurTimer.current);
                setOpen(false);
                if (r.email) onPick(r.email);
              }}
              disabled={!r.email}
              className="w-full text-left px-10 py-8 hover:bg-sunken disabled:opacity-50 border-b border-line-subtle last:border-b-0"
            >
              <div className="type-label-sm">{r.display_name || '—'}</div>
              <div className="text-11 text-ink-muted">{r.email || 'no email on file'}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- New account + referral code (one-shot) ---------- */

/** For influencers / partners who haven't signed up on the lander. Creates a
 *  passwordless portal account (Supabase user + Healthie chart + patients
 *  row) and mints their referral code in a single submit. Marketing can hand
 *  the resulting link straight to the influencer. Idempotent on email. */
function CreateAccountCard() {
  const create = useCreateAccountWithReferral();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [result, setResult] = useState<{
    code: string;
    link: string;
    email: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setCopied(false);
    const f = firstName.trim();
    const l = lastName.trim();
    const em = email.trim();
    if (!f || !l || !em) {
      setError('Fill in every field first.');
      return;
    }
    try {
      const out = await create.mutateAsync({
        first_name: f,
        last_name: l,
        email: em,
      });
      setResult({
        code: out.code,
        link: out.referral_link,
        email: out.email,
      });
      setFirstName('');
      setLastName('');
      setEmail('');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Could not create account.',
      );
    }
  }

  async function copyLink() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore — non-secure context */
    }
  }

  return (
    <div className="p-16 border border-line rounded-lg bg-raised">
      <h2 className="type-heading-md mb-4">Create new account + code</h2>
      <p className="type-label-sm text-ink-muted mb-12">
        Spins up a passwordless portal account for an influencer / partner who
        hasn&apos;t signed up via the lander, and mints their referral code.
        Idempotent — re-running for the same email returns the existing
        account and code. The customer can later sign in via magic link at{' '}
        <code className="font-mono text-12">/login</code>.
      </p>
      <form
        onSubmit={submit}
        className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-8"
      >
        <input
          type="text"
          placeholder="First name"
          autoComplete="off"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          disabled={create.isPending}
          className="px-12 py-6 type-label-sm border border-line rounded bg-page min-w-[10rem] sm:flex-1 focus:outline-none focus:border-ink"
        />
        <input
          type="text"
          placeholder="Last name"
          autoComplete="off"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          disabled={create.isPending}
          className="px-12 py-6 type-label-sm border border-line rounded bg-page min-w-[10rem] sm:flex-1 focus:outline-none focus:border-ink"
        />
        <input
          type="email"
          placeholder="Email"
          autoComplete="off"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={create.isPending}
          className="px-12 py-6 type-label-sm border border-line rounded bg-page min-w-[14rem] sm:flex-[2] focus:outline-none focus:border-ink"
        />
        <button
          type="submit"
          disabled={create.isPending}
          className="px-14 py-6 type-label-sm bg-ink text-ink-onAccent rounded hover:opacity-90 disabled:opacity-50"
        >
          {create.isPending ? 'Creating…' : 'Create account'}
        </button>
      </form>

      {error && (
        <p className="type-label-sm text-danger mt-12">{error}</p>
      )}

      {result && (
        <div className="mt-12 p-12 bg-page border border-line-subtle rounded flex flex-col gap-6">
          <div className="type-label-sm text-ink-muted">
            Account created for {result.email}:
          </div>
          <div className="flex flex-wrap items-center gap-10">
            <span className="font-mono font-semibold type-label-md">
              {result.code}
            </span>
            <a
              href={result.link}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-12 text-ink-muted hover:text-ink underline truncate"
            >
              {result.link}
            </a>
            <button
              type="button"
              onClick={copyLink}
              className="ml-auto inline-flex items-center gap-4 px-10 py-4 type-label-sm border border-line rounded hover:bg-sunken"
            >
              {copied ? (
                <>
                  <Check size={14} /> Copied
                </>
              ) : (
                <>
                  <Link2 size={14} /> Copy link
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
